import React, { useState, useEffect, useRef } from 'react';
import { useAppStore } from '../App';
import { formatTime } from '../utils/datetime';

const Chat: React.FC = () => {
 const { 
 user, 
 ws,
 chatMessages,
 activeChatTarget,
 chatTargets,
 unreadChatCounts,
 isChatOpen,
 setActiveChatTarget,
 setChatMessages,
 setChatTargets,
 markChatAsRead,
 setChatOpen,
 setUnreadCount
 } = useAppStore();
 
 const [inputMessage, setInputMessage] = useState('');
 const [searchQuery, setSearchQuery] = useState('');
 const [searchResults, setSearchResults] = useState<any[]>([]);
 const [isSearching, setIsSearching] = useState(false);
 const messagesEndRef = useRef<HTMLDivElement>(null);
 const [isMinimized, setIsMinimized] = useState(false);
 
 // 메시지 스크롤
 const scrollToBottom = () => {
 messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
 };
 
 useEffect(() => {
 scrollToBottom();
 }, [chatMessages, activeChatTarget]);
 
 // activeChatTarget이 변경되면 메시지 로드
 useEffect(() => {
 if (activeChatTarget && isChatOpen) {
 const messages = chatMessages.get(activeChatTarget.id);
 if (!messages || messages.length === 0) {
 loadChatHistory(activeChatTarget.id);
 }
 }
 }, [activeChatTarget, isChatOpen]);
 
 // 최근 대화 목록 로드
 useEffect(() => {
 if (isChatOpen) {
 loadRecentConversations();
 
 // 이미 선택된 대화가 있으면 메시지 로드
 if (activeChatTarget && (!chatMessages.get(activeChatTarget.id) || chatMessages.get(activeChatTarget.id)?.length === 0)) {
 loadChatHistory(activeChatTarget.id);
 }
 }
 }, [isChatOpen]);
 
 const loadRecentConversations = async () => {
 try {
 const response = await fetch('/chat/recent-conversations', {
 credentials: 'include'
 });
 
 if (response.ok) {
 const data = await response.json();
 const conversations = data.data || [];
 
 // targets 설정
 const targets = conversations.map((conv: any) => ({
 type: conv.type,
 id: conv.id,
 name: conv.name,
 isOnline: conv.isOnline
 }));
 setChatTargets(targets);
 
 // unreadCounts 설정
 conversations.forEach((conv: any) => {
 if (conv.unreadCount > 0) {
 setUnreadCount(conv.id, conv.unreadCount);
 }
 });
 }
 } catch (error) {
 console.error('Failed to load recent conversations:', error);
 }
 };
 
 // 채팅 기록 로드
 const loadChatHistory = async (targetId: string) => {
 try {
 const response = await fetch(`/chat/history?targetHash=${targetId}&limit=50`, {
 credentials: 'include'
 });
 
 if (response.ok) {
 const data = await response.json();
 setChatMessages(targetId, data.data);
 markChatAsRead(targetId);

 // 백엔드 읽음 처리: 내가 받은(상대가 보낸) 메시지를 chat_read_status 에 기록.
 // 이 호출이 없으면 /chat/recent-conversations 의 unread_count 가 계속 >0 으로 남아
 // 채팅창에서 읽어도 빨간 알림 배지가 사라지지 않는다.
 const unreadIds = (data.data || [])
 .filter((m: any) => m.senderHash && m.senderHash !== user?.moPeerHash)
 .map((m: any) => m.id)
 .filter(Boolean);
 if (unreadIds.length > 0) {
 try {
 await fetch('/chat/mark-read', {
 method: 'PUT',
 headers: { 'Content-Type': 'application/json' },
 credentials: 'include',
 body: JSON.stringify({ messageIds: unreadIds })
 });
 } catch (e) {
 console.error('Failed to mark chat as read:', e);
 }
 }

 // 메시지에서 발신자 정보를 추출하여 대화 상대 이름 업데이트
 if (data.data && data.data.length > 0) {
 const currentTarget = chatTargets.find(t => t.id === targetId);
 if (currentTarget && currentTarget.name === '알 수 없는 사용자') {
 // 상대방이 보낸 메시지 찾기
 const otherMessage = data.data.find((msg: any) => msg.senderHash === targetId);
 if (otherMessage && otherMessage.senderName) {
 const updatedTargets = chatTargets.map(t => 
 t.id === targetId 
 ? { ...t, name: otherMessage.senderName }
 : t
 );
 setChatTargets(updatedTargets);
 }
 }
 }
 }
 } catch (error) {
 console.error('Failed to load chat history:', error);
 }
 };
 
 // 대상 검색
 const searchTargets = async (query: string) => {
 if (query.length < 2) {
 setSearchResults([]);
 return;
 }
 
 setIsSearching(true);
 try {
 const response = await fetch(`/chat/search-targets?q=${encodeURIComponent(query)}`, {
 credentials: 'include'
 });
 
 if (response.ok) {
 const data = await response.json();
 setSearchResults(data.data);
 }
 } catch (error) {
 console.error('Failed to search targets:', error);
 } finally {
 setIsSearching(false);
 }
 };
 
 // 검색 디바운스
 useEffect(() => {
 const timer = setTimeout(() => {
 searchTargets(searchQuery);
 }, 300);
 
 return () => clearTimeout(timer);
 }, [searchQuery]);
 
 // 메시지 전송
 const sendMessage = async () => {
 if (!inputMessage.trim() || !activeChatTarget) return;
 
 try {
 const response = await fetch('/chat/send', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json' },
 credentials: 'include',
 body: JSON.stringify({
 recipientHash: activeChatTarget.type === 'peer' ? activeChatTarget.id : undefined,
 recipientFamilyHash: activeChatTarget.type === 'family' ? activeChatTarget.id : undefined,
 content: inputMessage,
 type: activeChatTarget.type
 })
 });
 
 if (response.ok) {
 setInputMessage('');
 }
 } catch (error) {
 console.error('Failed to send message:', error);
 }
 };
 
 // 대상 선택
 const selectTarget = (target: any) => {
 setActiveChatTarget(target);
 setSearchQuery('');
 setSearchResults([]);
 loadChatHistory(target.id);
 };
 
 // 총 읽지 않은 메시지 수
 const totalUnread = Array.from(unreadChatCounts.values()).reduce((sum, count) => sum + count, 0);
 
 if (!isChatOpen) {
 return (
 <div className="fixed bottom-4 right-4 z-50">
 <button
 onClick={async () => {
 setChatOpen(true);
 
 // chatTargets가 비어있으면 먼저 로드
 if (chatTargets.length === 0) {
 await loadRecentConversations();
 }
 
 // 읽지 않은 메시지가 있는 첫 번째 대화를 자동 선택
 setTimeout(() => {
 const targets = useAppStore.getState().chatTargets;
 const counts = useAppStore.getState().unreadChatCounts;
 
 // 대화 목록이 비어있지만 읽지 않은 메시지가 있는 경우
 if (counts.size > 0 && targets.length === 0) {
 // unreadCounts의 첫 번째 항목을 선택
 const [targetId, count] = counts.entries().next().value;
 if (targetId && count > 0) {
 // latestMessageSender 정보 확인
 const latestSender = useAppStore.getState().latestMessageSender;
 
 // 메시지에서 발신자 이름 찾기
 const messages = useAppStore.getState().chatMessages.get(targetId);
 let targetName = '알 수 없는 사용자';
 
 if (latestSender && latestSender.id === targetId) {
 targetName = latestSender.name;
 } else if (messages && messages.length > 0) {
 // 메시지에서 발신자 이름 찾기
 const messageFromTarget = messages.find(m => m.senderHash === targetId);
 if (messageFromTarget && messageFromTarget.senderName) {
 targetName = messageFromTarget.senderName;
 }
 }
 
 const tempTarget = {
 type: 'peer' as const,
 id: targetId,
 name: targetName,
 isOnline: false
 };
 // 대화 상대 목록에 추가
 setChatTargets([tempTarget]);
 // 대화 선택
 selectTarget(tempTarget);
 }
 } else if (totalUnread > 0 && targets.length > 0) {
 for (const target of targets) {
 const unreadCount = counts.get(target.id) || 0;
 if (unreadCount > 0) {
 selectTarget(target);
 break;
 }
 }
 }
 }, 100);
 }}
 className="rounded-full bg-white text-blue-600 p-2 shadow-lg hover:bg-gray-50 transition-colors relative"
 aria-label="채팅 열기"
 >
 <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
 d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
 </svg>
 {totalUnread > 0 && (
 <span className="absolute -top-1 -right-1 rounded-full bg-red-500 text-white text-xs w-6 h-6 flex items-center justify-center">
 {totalUnread}
 </span>
 )}
 </button>
 </div>
 );
 }
 
 return (
 <div className={`fixed bottom-4 right-4 bg-white shadow-2xl z-50 overflow-hidden transition-all ${
 isMinimized ? 'w-80 h-12' : 'w-96 h-[600px]'
 }`}>
 {/* 헤더 */}
 <div className="bg-blue-600 text-white p-3 flex items-center justify-between cursor-pointer"
 onClick={() => setIsMinimized(!isMinimized)}>
 <h3 className="font-semibold">채팅</h3>
 <div className="flex items-center gap-2">
 {totalUnread > 0 && (
 <span className="bg-red-500 text-white text-xs px-2 py-1">
 {totalUnread}
 </span>
 )}
 <button
 onClick={(e) => {
 e.stopPropagation();
 setChatOpen(false);
 }}
 className="hover:bg-blue-700 p-1"
 >
 <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
 </svg>
 </button>
 </div>
 </div>
 
 {!isMinimized && (
 <div className="flex h-[calc(100%-48px)]">
 {/* 대화 목록 */}
 <div className="w-2/5 border-r flex-shrink-0">
 {/* 검색 */}
 <div className="p-2 border-b">
 <input
 type="text"
 placeholder="검색..."
 value={searchQuery}
 onChange={(e) => setSearchQuery(e.target.value)}
 className="w-full px-3 py-1 border text-sm"
 />
 </div>
 
 {/* 대화 목록 */}
 <div className="overflow-y-auto h-[calc(100%-48px)]">
 {(searchQuery ? searchResults : chatTargets).map((target) => {
 const unreadCount = unreadChatCounts.get(target.id) || 0;
 return (
 <div
 key={target.id}
 onClick={() => selectTarget(target)}
 className={`p-3 border-b cursor-pointer hover:bg-gray-50 ${
 activeChatTarget?.id === target.id ? 'bg-blue-50' : ''
 }`}
 >
 <div className="flex items-center justify-between">
 <div className="flex items-center gap-2">
 <div className={`w-2 h-2 ${
 target.isOnline ? 'bg-green-500' : 'bg-gray-400'
 }`} />
 <span className="text-sm font-medium">{target.name}</span>
 </div>
 {unreadCount > 0 && (
 <span className="bg-red-500 text-white text-xs px-2 py-1">
 {unreadCount}
 </span>
 )}
 </div>
 </div>
 );
 })}
 </div>
 </div>
 
 {/* 채팅 영역 */}
 <div className="flex-1 flex flex-col min-w-0">
 {activeChatTarget ? (
 <>
 {/* 대화 상대 정보 */}
 <div className="p-3 border-b bg-gray-50">
 <div className="flex items-center gap-2">
 <div className={`w-3 h-3 ${
 activeChatTarget.isOnline ? 'bg-green-500' : 'bg-gray-400'
 }`} />
 <span className="font-medium">{activeChatTarget.name}</span>
 </div>
 </div>
 
 {/* 메시지 목록 */}
 <div className="flex-1 min-w-0 overflow-y-auto py-4 space-y-2">
 {(chatMessages.get(activeChatTarget.id) || []).map((message) => (
 <div
 key={message.id}
 className={`flex ${
 message.senderHash === user?.moPeerHash ? 'justify-end' : 'justify-start'
 }`}
 >
 <div className={`max-w-[70%] min-w-0 ${
 message.senderHash === user?.moPeerHash
 ? 'bg-blue-500 text-white'
 : 'bg-gray-200 text-gray-800'
 } px-3 py-2`}>
 <p className="text-sm break-words whitespace-pre-wrap">{message.content}</p>
 <p className={`text-xs mt-1 ${
 message.senderHash === user?.moPeerHash
 ? 'text-blue-100'
 : 'text-gray-500'
 }`}>
 {message.sentAt ? formatTime(message.sentAt) : ''}
 </p>
 </div>
 </div>
 ))}
 <div ref={messagesEndRef} />
 </div>
 
 {/* 메시지 입력 */}
 <div className="border-t p-3">
 <div className="flex gap-2">
 <input
 type="text"
 value={inputMessage}
 onChange={(e) => setInputMessage(e.target.value)}
 onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
 placeholder="메시지를 입력하세요..."
 className="flex-1 px-3 py-2 border"
 />
 <button
 onClick={sendMessage}
 className="bg-blue-600 text-white px-4 py-2 hover:bg-blue-700 transition-colors"
 >
 전송
 </button>
 </div>
 </div>
 </>
 ) : (
 <div className="flex-1 flex items-center justify-center text-gray-500">
 대화를 선택하세요
 </div>
 )}
 </div>
 </div>
 )}
 </div>
 );
};

export default Chat;